import { useSyncExternalStore } from 'react'
import { useQuery } from '@tanstack/react-query'
import type { PriceKind, RatesSnapshot } from '@globalx/core'
import { demoRates } from '@/data/demo'

/** Snapshot as served by the rates API (server adds cross-source checks). */
export interface LiveRates extends RatesSnapshot {
  provider?: string
  checks?: {
    comparedWith?: string
    deviations: { code: string; primary: string; secondary: string; diffPct: number }[]
  }
}

export type RatesStatus = 'loading' | 'ready' | 'error'

export interface RatesState {
  snapshot: LiveRates | null
  status: RatesStatus
  /** True when the shown snapshot comes from this device's cache because the API failed. */
  fromDeviceCache: boolean
  isFetching: boolean
  refetch: () => void
}

const RATES_URL = (import.meta.env.VITE_RATES_URL as string | undefined)?.trim() || '/api/rates'
export const USING_DEMO_RATES = RATES_URL === 'demo'
const CACHE_KEY = 'gx-rates-cache'
const KINDS: PriceKind[] = ['reference', 'delayed', 'live']

function isValidSnapshot(x: unknown): x is LiveRates {
  const s = x as LiveRates | null
  return (
    !!s &&
    typeof s.base === 'string' &&
    typeof s.asOf === 'string' &&
    !Number.isNaN(Date.parse(s.asOf)) &&
    // The API must never label data as demo, and demo data never comes from the API.
    KINDS.includes(s.kind) &&
    !!s.source?.name &&
    !!s.rates &&
    typeof s.rates === 'object' &&
    Object.values(s.rates).every((v) => typeof v === 'string')
  )
}

function readDeviceCache(): LiveRates | undefined {
  try {
    const raw = window.localStorage.getItem(CACHE_KEY)
    const parsed: unknown = raw ? JSON.parse(raw) : undefined
    return isValidSnapshot(parsed) ? parsed : undefined
  } catch {
    return undefined
  }
}

function writeDeviceCache(s: LiveRates) {
  try {
    window.localStorage.setItem(CACHE_KEY, JSON.stringify(s))
  } catch {
    /* storage unavailable: offline fallback simply won't exist */
  }
}

async function fetchRates(): Promise<LiveRates> {
  const res = await fetch(RATES_URL, { headers: { accept: 'application/json' } })
  if (!res.ok) throw new Error(`rates HTTP ${res.status}`)
  const body: unknown = await res.json()
  if (!isValidSnapshot(body)) throw new Error('rates: invalid payload')
  writeDeviceCache(body)
  return body
}

/**
 * Single entry point for fiat rates: server API (cached there), with the last good
 * snapshot kept on the device for offline use. Demo data only when explicitly configured.
 */
export function useRates(): RatesState {
  const query = useQuery({
    queryKey: ['rates', RATES_URL],
    queryFn: fetchRates,
    enabled: !USING_DEMO_RATES,
    staleTime: 5 * 60 * 1000,
    refetchInterval: 15 * 60 * 1000,
    retry: 2,
    // Last good snapshot from this device: shown immediately, kept if the network fails.
    initialData: readDeviceCache,
    initialDataUpdatedAt: 0,
  })

  if (USING_DEMO_RATES) {
    return { snapshot: demoRates, status: 'ready', fromDeviceCache: false, isFetching: false, refetch: () => {} }
  }
  const snapshot = query.data ?? null
  return {
    snapshot,
    status: snapshot ? 'ready' : query.isError ? 'error' : 'loading',
    fromDeviceCache: !!snapshot && query.dataUpdatedAt === 0,
    isFetching: query.isFetching,
    refetch: () => void query.refetch(),
  }
}

function subscribeOnline(cb: () => void) {
  window.addEventListener('online', cb)
  window.addEventListener('offline', cb)
  return () => {
    window.removeEventListener('online', cb)
    window.removeEventListener('offline', cb)
  }
}

export function useOnline(): boolean {
  return useSyncExternalStore(subscribeOnline, () => navigator.onLine, () => true)
}
